import { reorder } from "@atlaskit/pragmatic-drag-and-drop/reorder";
import type { Icon } from "@studio3t/generic-components";
import type { NormalizedJobState } from "@/pages/pipelineHelpers";
import type { MiddlewareConfig, Service, TestSuiteCase } from "@/types/api";
import {
  isLlmProvider,
  SINK_ONLY_PROVIDERS,
  SOURCE_ONLY_PROVIDERS,
} from "@/utils/providerCapabilities";
// Type-only: pipelineLint.ts imports canStageAs (via pipelineStaging's
// re-export) at runtime.
import type { LintStatus } from "./pipelineLint";

// The staging model: pure list transforms and id schemes behind the
// PipelineStagingProvider (pipelineStaging.tsx). Staging rules mirror
// /studio's ExplorerContextMenu.buildStageActions:
//   - udf scripts go to the TRANSFORM section only
//   - source-only providers (http_source routes, generator streams) can only
//     be a source — neither has a writer
//   - every other service resource can be a source or a sink
//   - SOURCE holds a single resource; TRANSFORM/SINK hold any number
//   - schema explorer leaves go to the SCHEMA section only, where each can
//     be attached to any number of staged sources/transforms/sinks
//   - test-suite explorer rows go to the TESTS section only

export type StagedRole = "source" | "transform" | "sink";
/** Everything the nav bar can hold — staging roles plus SCHEMA and TESTS. */
export type StagedKind = StagedRole | "schema" | "test";

/** A staged row's id — an explorer resource or schema id. */
export type StagedId = string;

export interface StagedResource {
  id: StagedId;
  name: string;
  icon: Icon;
  provider?: Service["provider"];
  /** The route the stage names in the pipeline code (`route_id`). It lives
   *  on the row so it follows the row: kept through a reorder or a rename,
   *  gone when the row is removed, and never inherited by a row staged
   *  fresh from the explorer. */
  routeId?: string;
}

// The stage tabs with V2 panels and per-tab unattached SCHEMA blocks.
export type StageTabRole = "source" | "sink" | "transform";

export interface StagedSchema extends StagedResource {
  /** Staged pipeline steps this schema validates — the "Attach to" dropdown
   *  toggles entries, so one schema can cover several steps. */
  attachments: Array<{ role: StagedRole; id: StagedId }>;
  /** The stage tabs whose bottom blocks list this schema as "the next
   *  block's schema" — independent of attachments, so a schema already
   *  serving other stages can wait in a bottom block too (blocks reuse
   *  schemas freely; a block is one optional stage + one schema). The tabs
   *  are unrelated: pending on the Sink tab never shows on Transform, and
   *  one schema can wait on several tabs at once — a Transform drop must
   *  not take the Source tab's waiting copy away. Absent or empty = waiting
   *  nowhere. */
  pending?: StageTabRole[];
}

/** Whether a staged schema waits in the given tab's bottom block. */
export function isPendingOn(schema: StagedSchema, tab: StageTabRole): boolean {
  return schema.pending?.includes(tab) ?? false;
}

/** Whether a staged schema waits in any tab's bottom block. */
export function isPendingAnywhere(schema: StagedSchema): boolean {
  return (schema.pending?.length ?? 0) > 0;
}

/** The schema without the given tab's waiting state — the key dropped
 *  entirely once it waits nowhere, so "not pending" has one shape. */
export function withoutPendingOn(
  schema: StagedSchema,
  tab: StageTabRole,
): StagedSchema {
  const rest = (schema.pending ?? []).filter((t) => t !== tab);
  const { pending: _cleared, ...bare } = schema;
  return rest.length > 0 ? { ...bare, pending: rest } : bare;
}

export function canStageAs(
  role: StagedRole,
  provider: Service["provider"] | undefined,
): boolean {
  if (!provider) return false;
  // LLM services back the AI assistant, not pipelines: no stage takes
  // them (the explorer hides them for the same reason), so neither the
  // code editor's suggestions nor the linter's "did you mean" offer them.
  if (isLlmProvider(provider)) return false;
  if (provider === "udf") return role === "transform";
  if (role === "transform") return false;
  if (role === "sink") return !SOURCE_ONLY_PROVIDERS.has(provider);
  return !SINK_ONLY_PROVIDERS.has(provider);
}

/** Drag eligibility for an explorer row: stageable into at least one section. */
export function canStageAnywhere(
  provider: Service["provider"] | undefined,
): boolean {
  return (["source", "transform", "sink"] as const).some((role) =>
    canStageAs(role, provider),
  );
}

/** Which half of the drop-target row the pointer is in. */
export type DropEdge = "top" | "bottom";

// Reorders a staged list by dropping `sourceId` onto `targetId`: "top" inserts
// above the target, "bottom" below. Returns the input list unchanged for
// no-op moves so React state setters can skip the update.
export function moveStagedItem<T extends { id: string }>(
  list: T[],
  sourceId: StagedId,
  targetId: StagedId,
  edge: DropEdge,
): T[] {
  const startIndex = list.findIndex((r) => r.id === sourceId);
  const targetIndex = list.findIndex((r) => r.id === targetId);
  if (startIndex === -1) return list;
  if (targetIndex === -1) return list;
  if (startIndex === targetIndex) return list;
  // Insertion slot in the original list, then compensated for the removal of
  // the dragged item when it sits before the slot.
  const position = edge === "top" ? targetIndex : targetIndex + 1;
  const finishIndex = position > startIndex ? position - 1 : position;
  if (finishIndex === startIndex) return list;
  return reorder({ list, startIndex, finishIndex });
}

// A pipeline reads from exactly one source, so SOURCE is a single slot:
// staging a new source replaces the current one. Other sections append.
// Re-staging an already staged id is a no-op everywhere.
export function withStaged(
  prev: StagedResource[],
  role: StagedRole,
  resource: StagedResource,
): StagedResource[] {
  if (prev.some((r) => r.id === resource.id)) return prev;
  return role === "source" ? [resource] : [...prev, resource];
}

/** Whether a stage tab's drop zone takes a resource right now — the
 *  explorer-drag gate (StageNavPanel), not the staging model: like the
 *  Tests panel's one suite, SOURCE offers no drop target while its slot is
 *  taken, so the staged source has to be removed deliberately before
 *  another can land (withStaged's replace stays for other callers).
 *  TRANSFORM and SINK append, so they always accept. */
export function stageAcceptsDrop(
  role: StageTabRole,
  staged: ReadonlyArray<{ id: string }>,
): boolean {
  return role !== "source" || staged.length === 0;
}

/** Whether the open pipeline is view-only — "Running pipelines can only be
 *  viewed. Stop the pipeline first if you need to make any edits." (Figma
 *  14894-157225): a deployed pipeline whose job runs or is still converging
 *  (Starting/Stopping). A stopped or failed job is editable again; scratch
 *  and drafts never lock. Every edit surface reads this one flag through
 *  the pipeline provider's editsLocked. */
export function editsLockedFor(
  openKind: "draft" | "deployed" | null,
  displayState: NormalizedJobState | null,
): boolean {
  if (openKind !== "deployed" || displayState === null) return false;
  return (
    displayState === "Running" ||
    displayState === "Starting" ||
    displayState === "Stopping"
  );
}

/** Whether an explorer row can be picked up for the view and tab on
 *  screen — only rows with somewhere to land (NewExplorer's drag hook):
 *  the Full pipeline view has no drop targets at all; the Tests tab takes
 *  suites; a stage tab takes what canStageAs admits for it — SOURCE only
 *  while its slot is empty (stageAcceptsDrop) — plus schemas, which every
 *  stage tab has a slot for. The staging locks (a staged resource, any
 *  suite once one is staged) stay the caller's. */
export function explorerDragAllowed(
  target: { view: StudioView; tab: StageTabId },
  item: {
    provider: Service["provider"] | undefined;
    isSchema: boolean;
    isTest: boolean;
  },
  sources: ReadonlyArray<{ id: string }>,
): boolean {
  if (target.view === "full") return false;
  if (target.tab === "tests") return item.isTest;
  if (item.isTest) return false;
  if (item.isSchema) return true;
  if (!canStageAs(target.tab, item.provider)) return false;
  return target.tab !== "source" || stageAcceptsDrop("source", sources);
}

// Staged tests append; duplicates by id are no-ops.
export function withStagedTest(
  prev: StagedResource[],
  resource: StagedResource,
): StagedResource[] {
  if (prev.some((r) => r.id === resource.id)) return prev;
  return [...prev, resource];
}

// Dropping a schema into a tab's bottom block makes it that block's
// pending schema — the "next block" being assembled. Works for already
// staged schemas too, attached or waiting elsewhere: blocks reuse schemas
// freely, so TEST can serve sink A's block AND wait in the bottom block
// for sink B, and wait on the Source tab AND on Transform at once.
export function withStagedSchema(
  prev: StagedSchema[],
  resource: StagedResource,
  tab: StageTabRole = "sink",
): StagedSchema[] {
  const existing = prev.find((s) => s.id === resource.id);
  if (existing) {
    if (isPendingOn(existing, tab)) return prev;
    return prev.map((s) =>
      s.id === resource.id ? { ...s, pending: [...(s.pending ?? []), tab] } : s,
    );
  }
  return [...prev, { ...resource, attachments: [], pending: [tab] }];
}

// A sink holds at most one schema: attaching to a taken target evicts the
// incumbent, marked so the caller can unstage it once nothing else
// references it.
function withAttachmentTo(
  s: StagedSchema,
  resource: StagedResource,
  target: { role: StagedRole; id: StagedId },
  evicted: Set<string>,
): StagedSchema {
  if (s.id === resource.id) {
    if (s.attachments.some((a) => a.id === target.id)) return s;
    // A waiting copy stays pending: blocks reuse schemas freely, so a
    // slot drop must not steal the bottom block's schema.
    return { ...s, attachments: [...s.attachments, target] };
  }
  if (!s.attachments.some((a) => a.id === target.id)) return s;
  evicted.add(s.id);
  return {
    ...s,
    attachments: s.attachments.filter((a) => a.id !== target.id),
  };
}

// Stages a schema (duplicates by id ignored) and makes sure it's attached to
// the given step — the sink-tab per-sink SCHEMA drop, where the drop target
// names the attachment. Unlike toggleAttachment this never detaches the
// dropped schema itself. Any other schema attached to the target is
// detached, and unstaged once nothing else references it.
export function withStagedSchemaAttached(
  prev: StagedSchema[],
  resource: StagedResource,
  target: { role: StagedRole; id: StagedId },
): StagedSchema[] {
  const evicted = new Set<string>();
  const exists = prev.some((s) => s.id === resource.id);
  const staged = exists
    ? prev
    : [...prev, { ...resource, attachments: [] } as StagedSchema];
  return staged
    .map((s) => withAttachmentTo(s, resource, target, evicted))
    .filter(
      (s) =>
        !(
          evicted.has(s.id) &&
          s.attachments.length === 0 &&
          !isPendingAnywhere(s)
        ),
    );
}

// A schema dropped or written before its stage is waiting in that tab's
// bottom block — a newly staged sink/transform adopts it (the pending one,
// one schema per stage) so the pair shares one group instead of starting a
// new block. Adoption consumes the pending state; other attachments the
// schema already has are untouched.
export function schemasAfterStageStaged(
  prev: StagedSchema[],
  role: StageTabRole,
  stageId: StagedId,
): StagedSchema[] {
  // Re-dropping an already-set stage must not adopt a second schema.
  if (prev.some((s) => s.attachments.some((a) => a.id === stageId)))
    return prev;
  const waiting = prev.find((s) => isPendingOn(s, role));
  if (!waiting) return prev;
  return prev.map((s) => {
    if (s.id !== waiting.id) return s;
    // Only this tab's waiting state is consumed — the schema may still be
    // waiting on another tab for that tab's next block.
    return {
      ...withoutPendingOn(s, role),
      attachments: [...s.attachments, { role, id: stageId }],
    };
  });
}

// The first staged sink/transform still lacking a schema, or null — the
// target for a schema drafted in the bottom block while its "future stage"
// already arrived.
export function firstSchemalessStageId(
  items: StagedResource[],
  schemas: StagedSchema[],
  role: StageTabRole,
): string | null {
  const withSchema = new Set(
    schemas.flatMap((s) =>
      s.attachments.filter((a) => a.role === role).map((a) => a.id),
    ),
  );
  return items.find((item) => !withSchema.has(item.id))?.id ?? null;
}

// Toggles one target on a staged schema: selecting an unattached step adds
// it, selecting an attached one removes it. Unknown schema ids leave the
// list untouched.
export function toggleAttachment(
  prev: StagedSchema[],
  schemaId: StagedId,
  target: { role: StagedRole; id: StagedId },
): StagedSchema[] {
  if (!prev.some((s) => s.id === schemaId)) return prev;
  return prev.map((s) => {
    if (s.id !== schemaId) return s;
    const attached = s.attachments.some((a) => a.id === target.id);
    return {
      ...s,
      attachments: attached
        ? s.attachments.filter((a) => a.id !== target.id)
        : [...s.attachments, target],
    };
  });
}

// Renames a staged schema everywhere it shows (every block renders the same
// underlying schema entry). Unknown ids leave the list untouched.
export function withSchemaRenamed(
  prev: StagedSchema[],
  schemaId: StagedId,
  name: string,
): StagedSchema[] {
  if (!prev.some((s) => s.id === schemaId && s.name !== name)) return prev;
  return prev.map((s) => (s.id === schemaId ? { ...s, name } : s));
}

// Renames a staged transform. Unlike schemas, a script's NAME is its
// resource id (the filename), so the rename changes the staged id too —
// schema attachments pointing at the old id follow it. Unknown ids leave
// both lists untouched.
export function withTransformRenamed(
  transforms: StagedResource[],
  schemas: StagedSchema[],
  oldId: StagedId,
  next: { id: StagedId; name: string; provider?: StagedResource["provider"] },
): { transforms: StagedResource[]; schemas: StagedSchema[] } {
  if (!transforms.some((t) => t.id === oldId)) return { transforms, schemas };
  return {
    transforms: transforms.map((t) =>
      t.id === oldId
        ? {
            ...t,
            id: next.id,
            name: next.name,
            provider: next.provider ?? t.provider,
          }
        : t,
    ),
    schemas: schemas.map((s) =>
      s.attachments.some((a) => a.id === oldId)
        ? {
            ...s,
            attachments: s.attachments.map((a) =>
              a.id === oldId ? { ...a, id: next.id } : a,
            ),
          }
        : s,
    ),
  };
}

// A schema losing its LAST attachment (and not already waiting anywhere)
// falls back to pending on the removed stage's tab — it stays staged, so
// it must stay visible somewhere.
function withStageDetached(s: StagedSchema, id: StagedId): StagedSchema {
  const removed = s.attachments.find((a) => a.id === id);
  if (!removed) return s;
  const attachments = s.attachments.filter((a) => a.id !== id);
  if (attachments.length > 0 || isPendingAnywhere(s)) {
    return { ...s, attachments };
  }
  return { ...s, attachments, pending: [removed.role] };
}

// SOURCE is a single slot, so a source drop replaces the incumbent —
// schemas attached to the replaced source detach and fall back to the
// source tab's bottom block (withStageDetached), where the incoming
// source's adoption pass (schemasAfterStageStaged) picks them up: the
// schema follows the replacement.
export function schemasAfterSourceReplaced(
  prev: StagedSchema[],
  replacedIds: StagedId[],
): StagedSchema[] {
  const next = prev.map((s) =>
    replacedIds.reduce((schema, id) => withStageDetached(schema, id), s),
  );
  return next.every((s, i) => s === prev[i]) ? prev : next;
}

// Unstaging drops the schema itself and detaches the removed resource from
// any schema that pointed at it.
export function schemasAfterUnstage(
  prev: StagedSchema[],
  id: StagedId,
): StagedSchema[] {
  const references = (s: StagedSchema) =>
    s.id === id || s.attachments.some((a) => a.id === id);
  if (!prev.some(references)) return prev;
  return prev.filter((s) => s.id !== id).map((s) => withStageDetached(s, id));
}

export type StudioView = "stages" | "full";

/** Whether there is anything to clear — staged items in any section,
 *  code-view edits, or content in the Source input / schema editors
 *  ("Clear and start fresh", "Save as draft" and "Deploy pipeline" all
 *  enable on this). A draft whose text equals its base is not an edit; a
 *  stale draft (based on outdated staging) still counts — clearing it is
 *  harmless. An editor counts whenever it shows anything but its
 *  placeholder — typed, pasted or a loaded sample/definition alike:
 *  visible content is content to clear or save. Unsaved SCHEMA slot drafts
 *  count too: they outlive the editor showing them (they stay in their
 *  block across selection changes). A staged test suite counts as soon as
 *  it is added — pending and empty included: it is content to clear, and
 *  the tests drafted under it ride on it. */
export function pipelineIsDirty(state: {
  sources: StagedResource[];
  transforms: StagedResource[];
  sinks: StagedResource[];
  schemas: StagedSchema[];
  tests: StagedResource[];
  draft: { text: string; basedOn: string } | null;
  inputDocument: string;
  schemaDocument: string;
  schemaDrafts: Record<string, string>;
  stagedSuite: { suiteId: string | null; name: string } | null;
}): boolean {
  return (
    state.sources.length > 0 ||
    state.transforms.length > 0 ||
    state.sinks.length > 0 ||
    state.schemas.length > 0 ||
    state.tests.length > 0 ||
    state.stagedSuite !== null ||
    (state.draft !== null && state.draft.text !== state.draft.basedOn) ||
    state.inputDocument !== "" ||
    state.schemaDocument !== "" ||
    Object.values(state.schemaDrafts).some((text) => text !== "")
  );
}

/** Whether Run must wait for the pipeline-code lint pass: only in the Full
 *  pipeline view, where the code draft is what would run — a pass still in
 *  flight or one that settled with diagnostics blocks. The Stages view runs
 *  off the staged panels, and the last lint verdict may be stale there. */
export function runBlockedByLint(
  view: StudioView,
  status: LintStatus,
  schemasPending = false,
): boolean {
  // A pending schemas block means the generated doc carries schema refs
  // with no block — wrong in EITHER view, so this blocks unconditionally.
  if (schemasPending) return true;
  return view === "full" && status !== "clean";
}

/** Tooltip for the disabled Run/Deploy buttons: null while nothing blocks
 *  (tooltip hidden), otherwise why the pipeline can't run yet. */
export function lintBlockReason(
  view: StudioView,
  status: LintStatus,
  schemasPending = false,
): string | null {
  if (!runBlockedByLint(view, status, schemasPending)) return null;
  if (schemasPending) return "Waiting for services to load";
  if (status === "empty") return "The pipeline is empty";
  if (status === "placeholders") return "Fill in the placeholders first";
  return status === "pending"
    ? "Waiting for pipeline checks to finish"
    : "The pipeline contains errors";
}

// Providers whose resources can't be introspected — clicking them shows no
// sample (same skip list as /studio's useResourceSelect; udf leaves are
// scripts, not documents, and the generator's documents come from the
// schema the pipeline already names).
const SKIP_INTROSPECTION_PROVIDERS = new Set<Service["provider"]>([
  "http_source",
  "http",
  "generator",
]);

/** Whether clicking an explorer resource can preview a sample document. */
export function canPreviewSample(
  provider: Service["provider"] | undefined,
): boolean {
  return (
    provider !== undefined &&
    provider !== "udf" &&
    !SKIP_INTROSPECTION_PROVIDERS.has(provider)
  );
}

/** Whether clicking an explorer leaf can preview a script (udf resources). */
export function canPreviewScript(
  provider: Service["provider"] | undefined,
): boolean {
  return provider === "udf";
}

// Stage tab of the Stages view (StageTabs.tsx renders them). Lives in the
// staging context so the explorer can adapt what a tree click does to the
// visible tab.
export type StageTabId = "source" | "transform" | "sink" | "tests";

// Selection ids of the "use the editor" pseudo-rows — fixed, so they can
// never collide with real resource ids.
export const SOURCE_EDITOR_ID = "source:editor";
export const TRANSFORM_EDITOR_ID = "transform:editor";
export const SCHEMA_EDITOR_ID = "schema:editor";
// The sink tab's "Drag a/another sink from the explorer" row. It owns no
// editor (the panel shows the no-editor message) — the id exists so the row
// can hold the selection like the other pseudo-rows.
export const SINK_EDITOR_ID = "sink:editor";

// Each SCHEMA sub-section on the sink/transform tabs has its own editor
// pseudo-row — one per staged stage item, plus the unattached one (Figma
// 14398-200858). The stage id rides in the selection id so a future save
// can attach the schema.
export function schemaEditorId(stageId?: string | null): string {
  return stageId ? `${SCHEMA_EDITOR_ID}:${stageId}` : SCHEMA_EDITOR_ID;
}

// The bottom (unattached) SCHEMA block's editor row, one per stage tab —
// the tabs are unrelated, so their bottom slots and drafts are too. The
// sink tab keeps the legacy bare id; "@" can't appear in staged ids, so
// the source/transform slots can't collide with a per-item slot.
export function unattachedSchemaEditorId(role: StageTabRole): string {
  return role === "sink" ? SCHEMA_EDITOR_ID : `${SCHEMA_EDITOR_ID}:@${role}`;
}

export function isSchemaEditorId(id: string | null): boolean {
  return id?.startsWith(SCHEMA_EDITOR_ID) ?? false;
}

// A schema can sit in several blocks at once (attached to stages, waiting
// in a bottom block); each row is its own selectable instance. Selection
// ids carry the block: "<schemaId>|<blockKey>", so selecting one row never
// selects the schema's other rows, and the dirty asterisk stays on the
// instance being edited. "|" appears in no staged id.
export function schemaInstanceId(schemaId: StagedId, blockKey: string): string {
  return `${schemaId}|${blockKey}`;
}

/** The schema id inside an instance selection id, or null for other ids. */
export function schemaIdOfInstance(id: string | null): string | null {
  if (!id) return null;
  const sep = id.indexOf("|");
  return sep === -1 ? null : id.slice(0, sep);
}

/** The three Studio editor panels. */
export type EditorKind = "input" | "script" | "schema";

// Whether the pipeline input document has been edited (Figma 14778-123822):
// it diverges from the introspected sample it was loaded from (origin), or
// was free-typed with no sample loaded. The empty editor is the zero state,
// never "edited" — and emptying it removes the staged source (inputEmptied).
// Drives the loadSample clobber guard; the source row's badge and info alert
// go through inputEditedSinceOpen.
export function inputEdited(doc: string, origin: string | null): boolean {
  return doc !== "" && doc !== origin;
}

// The input document just reached the empty placeholder by the user's hand
// — typed or cut away, the Clean button, ⌘Z past the sample fill (TTTL-254).
// The staged source goes with it, as its row's remove button would take it:
// an empty input leaves the source nothing to show. Whitespace is content;
// only the placeholder counts.
export function inputEmptied(previous: string, next: string): boolean {
  return previous !== "" && next === "";
}

// What selecting the source row does to the input editor (the sample
// loader, editorContents.useSampleLoader): an edited document always
// stays. A saved/drafted pipeline (savedInput non-empty) never
// regenerates its input — the empty-editor revert brings back the
// pipeline's stored input, an on-screen document is left alone. Only
// scratch fills from the introspected sample (the pre-TTTL-191 rule).
export function inputFillOnSelect(
  doc: string,
  origin: string | null,
  savedInput: string,
): "keep" | "restore-saved" | "load-sample" {
  if (inputEdited(doc, origin)) return "keep";
  if (savedInput !== "") return doc === "" ? "restore-saved" : "keep";
  return "load-sample";
}

// The "input edited" badge and alert (TTTL-191): a workspace input rides
// back in on open with no sample origin to match, so inputEdited alone
// would warn on a pipeline nobody touched this session. The badge also
// compares against the input the open pipeline started from (or was last
// saved with) — openedInput, "" for scratch — and only a document that
// diverges from both counts. The clobber guard stays on inputEdited: the
// stored input must not be overwritten by a fresh sample on source select.
export function inputEditedSinceOpen(
  doc: string,
  origin: string | null,
  openedInput: string,
): boolean {
  return inputEdited(doc, origin) && doc !== openedInput;
}

// Whether the selection sits anywhere inside the removed row's block: the
// row itself, one of the removed schema's per-block instances, or — for a
// stage row — its SCHEMA slot rows (the slot pseudo-row and any schema
// instance keyed under the block). Without the block match, the removed
// stage's editor document would survive hidden and resurface once the
// selection later falls back to the tab default.
export function unstageHoldsSelection(
  selectedId: string | null,
  id: StagedId,
): boolean {
  if (selectedId === null) return false;
  if (selectedId === id) return true;
  if (schemaIdOfInstance(selectedId) === id) return true;
  const slot = schemaEditorId(id);
  if (selectedId === slot) return true;
  const sep = selectedId.indexOf("|");
  return sep !== -1 && selectedId.slice(sep + 1) === slot;
}

// Where the selection lands when a stage tab is entered with nothing
// remembered (useTabMemory): the tab's first staged row, selected like a
// click so its content lands in the editor — the same landing view-only
// mode always shows — or the tab's editor pseudo-row while the tab is
// empty (the Figma default frame). The Tests tab keeps its own state
// (testEditorId) and picks its first row at the tab-switch site.
export function tabDefaultRow(
  tab: StageTabRole,
  lists: {
    sources: StagedResource[];
    transforms: StagedResource[];
    sinks: StagedResource[];
  },
): string {
  const items = {
    source: lists.sources,
    transform: lists.transforms,
    sink: lists.sinks,
  }[tab];
  const editorRow = {
    source: SOURCE_EDITOR_ID,
    transform: TRANSFORM_EDITOR_ID,
    sink: SINK_EDITOR_ID,
  }[tab];
  return items[0]?.id ?? editorRow;
}

// The bottom (unattached) slots' "@" markers and the row that takes the
// selection when such a block empties. The sink tab has no stage editor
// row, so its slot keeps the bare id and never reaches this table.
const UNATTACHED_SLOT_ROW: Record<string, string> = {
  "@source": SOURCE_EDITOR_ID,
  "@transform": TRANSFORM_EDITOR_ID,
};

// The row heading a SCHEMA slot's block: the owner stage row for
// "schema:editor:<stageId>", the tab's stage editor pseudo-row for the
// "@" (unattached) markers. Null for the bare sink slot, an empty or
// unknown marker, or a non-slot id.
function schemaSlotOwnerRow(slot: string): string | null {
  const prefix = `${SCHEMA_EDITOR_ID}:`;
  if (!slot.startsWith(prefix)) return null;
  const stageId = slot.slice(prefix.length);
  return stageId.startsWith("@")
    ? (UNATTACHED_SLOT_ROW[stageId] ?? null)
    : stageId || null;
}

// Where the selection lands when a schema leaves its block: for an
// instance id "<schemaId>|schema:editor:<stageId>" the block's owner stage
// row keeps it; for the bottom (unattached) slots the block is now empty,
// so its first item — the tab's stage editor pseudo-row — takes it
// (UNATTACHED_SLOT_ROW). Anything else — a non-instance id, a slot that is
// not a per-block SCHEMA editor row, an empty or unknown marker — falls
// back to null.
function selectionAfterSchemaLeavesBlock(
  selectedId: string | null,
): string | null {
  const schemaId = schemaIdOfInstance(selectedId);
  if (selectedId === null || schemaId === null) return null;
  return schemaSlotOwnerRow(selectedId.slice(schemaId.length + 1));
}

// Deleting the selected staged row hands the selection to its section's own
// pseudo-row and resets the editor that row owned — never a sibling row:
// SOURCE/TRANSFORM fall back to their "use the editor" rows and clear their
// editors, SINK to its drag-invitation row (it owns no editor, so nothing
// is cleared). Classic-bar test rows have no pseudo-row — the selection
// returns to the default fallback (null → staged source row or the SOURCE
// editor row) and no editor is touched (the tests editor runs on its own
// testEditorId state). Exception: a schema deleted from inside a stage's
// block hands the selection back to the block's owner row — falling to null
// would sever the selection's link to the stage whose document is still on
// screen, so deleting that stage next would skip the editor reset and its
// stale script/sample would resurface.
export function selectionAfterUnstage(
  role: StagedKind,
  selectedId: string | null = null,
): {
  selectedId: string | null;
  clearEditor: EditorKind | null;
} {
  if (role === "transform") {
    return { selectedId: TRANSFORM_EDITOR_ID, clearEditor: "script" };
  }
  if (role === "schema") {
    return {
      selectedId: selectionAfterSchemaLeavesBlock(selectedId),
      clearEditor: "schema",
    };
  }
  if (role === "source") {
    return { selectedId: SOURCE_EDITOR_ID, clearEditor: "input" };
  }
  if (role === "sink") {
    return { selectedId: SINK_EDITOR_ID, clearEditor: null };
  }
  return { selectedId: null, clearEditor: null };
}

// The stage editor / drag-invitation pseudo-rows are deselectTarget's
// selection floor: landing on one of these already means null (the row
// stays selected) — there's nowhere further down to fall.
const SELECTION_FLOOR_IDS = new Set<string>([
  SOURCE_EDITOR_ID,
  TRANSFORM_EDITOR_ID,
  SINK_EDITOR_ID,
]);

// A SCHEMA slot id's owner row for deselection, with the sink tab's bare
// legacy id special-cased to its drag-invitation row: schemaSlotOwnerRow's
// table only knows the "@source"/"@transform" unattached markers, since the
// bare sink slot carries no prefix for it to look up.
function schemaSlotOwnerRowOrSink(slot: string): string | null {
  if (slot === SCHEMA_EDITOR_ID) return SINK_EDITOR_ID;
  return schemaSlotOwnerRow(slot);
}

// Where a deselected staged stage row's own selection lands — schema and
// test roles (and any future addition) fall through to deselectTarget's
// null default rather than needing an entry here.
const ROLE_EDITOR_ROW: Partial<Record<StagedKind, string>> = {
  source: SOURCE_EDITOR_ID,
  transform: TRANSFORM_EDITOR_ID,
  sink: SINK_EDITOR_ID,
};

// Something is always selected: deselecting a staged stage row (its second
// click) hands the selection to the tab's own pseudo-row instead of
// dropping to null, and deselecting a schema row — a staged instance or an
// empty SCHEMA slot pseudo-row — hands it to the row heading its block:
// the owner source/script/sink row, or the tab's pseudo-row for the
// bottom (unattached) slots. The stage editor / drag-invitation pseudo-rows
// themselves are the selection floor: null = the row stays selected.
// The source tab never deselects to an emptied input (TTTL-191): the
// source row's deselect used to reset the input editor. Instead, clicking
// the selected source-side row toggles with its schema-side row: a staged
// source flips to its schema instance (or its empty SCHEMA slot), and
// with no source staged the editor pseudo-row flips to the bottom SCHEMA
// slot — the pending schema's row, or the empty slot itself. The schema
// rows' existing deselect already flips back to the row heading their
// block, closing the loop.
export function deselectTarget(
  id: string,
  roleOf: (id: string) => StagedKind | null,
  lists: { sources: StagedResource[]; schemas: StagedSchema[] },
): string | null {
  // Untitled script rows deselect to the pseudo-row like staged rows do.
  if (isUntitledScriptId(id)) return TRANSFORM_EDITOR_ID;
  if (id === SOURCE_EDITOR_ID) return sourcePseudoRowTarget(lists);
  if (SELECTION_FLOOR_IDS.has(id)) return null;
  if (isSchemaEditorId(id)) return schemaSlotOwnerRowOrSink(id);
  const schemaId = schemaIdOfInstance(id);
  if (schemaId !== null) {
    return schemaSlotOwnerRowOrSink(id.slice(schemaId.length + 1));
  }
  const role = roleOf(id);
  if (role === "source") return sourceRowSchemaTarget(id, lists.schemas);
  return role ? (ROLE_EDITOR_ROW[role] ?? null) : null;
}

// The source editor pseudo-row's toggle side: with no source staged, the
// bottom SCHEMA slot — the pending schema's row when one sits there, the
// empty slot itself otherwise. With a source staged the pseudo-row is
// the selection floor (null).
function sourcePseudoRowTarget(lists: {
  sources: StagedResource[];
  schemas: StagedSchema[];
}): string | null {
  if (lists.sources.length > 0) return null;
  const slot = unattachedSchemaEditorId("source");
  const pending = lists.schemas.find((s) => isPendingOn(s, "source"));
  return pending ? schemaInstanceId(pending.id, slot) : slot;
}

// The staged source row's toggle side: its attached schema's instance,
// or its empty SCHEMA slot.
function sourceRowSchemaTarget(id: string, schemas: StagedSchema[]): string {
  const schema = schemas.find((s) =>
    s.attachments.some((a) => a.role === "source" && a.id === id),
  );
  return schema
    ? schemaInstanceId(schema.id, schemaEditorId(id))
    : schemaEditorId(id);
}

// Which editor a fresh entry into an editor pseudo-row resets — re-clicks
// keep what was typed.
export function editorToReset(
  id: string,
  current: string | null,
): EditorKind | null {
  if (current === id) return null;
  if (id === SOURCE_EDITOR_ID) return "input";
  if (id === TRANSFORM_EDITOR_ID || isUntitledScriptId(id)) return "script";
  if (isSchemaEditorId(id)) return "schema";
  return null;
}

// _id first, then alphabetical — the sample documents' canonical key order.
function keyOrderCompare(a: string, b: string): number {
  if (a === "_id") return -1;
  if (b === "_id") return 1;
  return a.localeCompare(b);
}

// Temp fix for the engine's shuffled introspection order: mstream builds
// schema properties from an unsorted HashSet (builder.rs get_fields_at_level)
// and serde_json's preserve_order carries that random order into
// /schema/fill, so every introspection reshuffles the sample document.
// Canonicalize client-side — _id first, then alphabetical, recursively —
// until the engine returns first-seen document order.
export function stableKeyOrder(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableKeyOrder);
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([a], [b]) => keyOrderCompare(a, b))
        .map(([key, child]) => [key, stableKeyOrder(child)]),
    );
  }
  return value;
}

// Explorer resource ids are `resource:<service>:<resource>` (explorerData).
export function parseResourceId(
  id: string,
): { serviceName: string; resourceName: string } | null {
  const [kind, serviceName, ...rest] = id.split(":");
  if (kind !== "resource") return null;
  if (!serviceName) return null;
  if (rest.length === 0) return null;
  return { serviceName, resourceName: rest.join(":") };
}

// The deployed pipelines whose middlewares reference a staged transform's
// script — the same (service, filename) match the engine's delete guard
// runs. A non-empty result locks the row: no rename, no removal, no edits.
export function pipelinesUsingResource(
  jobs: ReadonlyArray<{
    name: string;
    pipeline: {
      middlewares?: Array<Pick<MiddlewareConfig, "service_name" | "resource">>;
    } | null;
  }>,
  resourceId: StagedId,
): string[] {
  const target = parseResourceId(resourceId);
  if (!target) return [];
  return jobs
    .filter((job) =>
      (job.pipeline?.middlewares ?? []).some(
        (m) =>
          m.service_name === target.serviceName &&
          m.resource === target.resourceName,
      ),
    )
    .map((job) => job.name);
}

// ---------------------------------------------------------------------------
// Tests tab: the new-test draft (the "Untitled test*" row)
// ---------------------------------------------------------------------------

// Old /studio's assertion operators (TestsPanel getAssertOperator). The old
// panel narrowed the list by the sampled value's type, but a draft has no
// value to sample yet, so every operator is on offer.
export type TestAssertionOp =
  | "equals"
  | "not_equals"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "contains"
  | "type_matches";

/** The operator glyphs shown wherever assertions render — the editor's
 *  dropdown (which derives its options from this order), the read-only
 *  viewer and the tree's hover preview. */
export const TEST_OPERATOR_LABELS: Record<TestAssertionOp, string> = {
  equals: "==",
  not_equals: "!=",
  gt: ">",
  gte: ">=",
  lt: "<",
  lte: "<=",
  contains: "contains",
  type_matches: "type",
};

/** One draft assertion line of the Tests editor (old /studio's
 *  TestAssertion: path, assertType, expectedValue — as editable strings). */
export type TestAssertionDraft = {
  key: number;
  path: string;
  op: TestAssertionOp;
  value: string;
};

// React list keys for draft lines — unique per session is all they need.
let assertionKeySeq = 0;

export function freshAssertionDraft(): TestAssertionDraft {
  return { key: assertionKeySeq++, path: "", op: "equals", value: "" };
}

// Untitled drafts are unbounded — every "use the editor / add a new one"
// cycle mints a fresh id, so any number of Untitled script/test rows can
// coexist. The ids are draft-map keys AND selection row ids.
export const UNTITLED_SCRIPT_PREFIX = `${TRANSFORM_EDITOR_ID}:untitled:`;
export const UNTITLED_TEST_PREFIX = "test:editor:untitled:";

let untitledSeq = 0;

export function freshUntitledScriptId(): string {
  return `${UNTITLED_SCRIPT_PREFIX}${untitledSeq++}`;
}

export function freshUntitledTestId(): string {
  return `${UNTITLED_TEST_PREFIX}${untitledSeq++}`;
}

export function isUntitledScriptId(id: string | null): boolean {
  return id?.startsWith(UNTITLED_SCRIPT_PREFIX) ?? false;
}

export function isUntitledTestId(id: string | null): boolean {
  return id?.startsWith(UNTITLED_TEST_PREFIX) ?? false;
}

/** Tests that are FINAL — saved to the suite with content and a proper
 *  name. Untitled drafts are not tests yet, so every counter (the Tests
 *  stage tab, the suite header badge) counts through here. */
export function countSavedTests(tests: ReadonlyArray<{ id: string }>): number {
  return tests.filter((t) => !isUntitledTestId(t.id)).length;
}

// The untitled ids present in a draft map, in creation order (the numeric
// suffix is the global mint counter).
const untitledIdsIn = (keys: string[], prefix: string) =>
  keys
    .filter((k) => k.startsWith(prefix))
    .sort(
      (a, b) => Number(a.slice(prefix.length)) - Number(b.slice(prefix.length)),
    );

export function untitledScriptIds(drafts: Record<string, unknown>): string[] {
  return untitledIdsIn(Object.keys(drafts), UNTITLED_SCRIPT_PREFIX);
}

export function untitledTestIds(drafts: Record<string, unknown>): string[] {
  return untitledIdsIn(Object.keys(drafts), UNTITLED_TEST_PREFIX);
}

/** The display name for the n-th untitled row (0-based). */
export function untitledName(base: string, index: number): string {
  return index === 0 ? base : `${base} ${index + 1}`;
}

/** True for names still wearing the mint pattern ("Untitled test",
 *  "Untitled script 2", …) — an untitled row's label carries its star
 *  only while this holds: a custom-named draft reads clean. */
export function isDefaultUntitledName(name: string, base: string): boolean {
  return new RegExp(`^${base}( \\d+)?$`).test(name);
}

/** Transforms that COUNT: saved scripts always (a stored file is never
 *  nothing), an untitled draft only once it has content — an empty
 *  script is not a script yet. */
export function countNonEmptyScripts(
  transforms: ReadonlyArray<{ id: string }>,
  scriptDrafts: Record<string, string>,
): number {
  return transforms.filter(
    (t) =>
      !isUntitledScriptId(t.id) || (scriptDrafts[t.id] ?? "").trim() !== "",
  ).length;
}

/** An untitled script draft's staged-row face — it lives IN the transforms
 *  list so drafts keep their creation order among dropped scripts and
 *  reorder with the same drag handles. The icon is the enum's literal
 *  value: importing the Icon enum's runtime value here would pull the
 *  component library into this module's (test-visible) import graph. */
export function untitledScriptResource(
  id: string,
  name: string,
): StagedResource {
  return { id, name, icon: "Code" as StagedResource["icon"] };
}

/** An untitled test draft's staged-row face — same deal for the tests
 *  list: drafts keep their creation order among dropped suites. */
export function untitledTestResource(id: string, name: string): StagedResource {
  return { id, name, icon: "Tests" as StagedResource["icon"] };
}

/** The Tests tab's pseudo-row as editor owner — the state after the edited
 *  test is deleted or the untitled draft discarded: the editor shows its
 *  links (zero) face and the pseudo-row carries the active highlight, the
 *  same hand-off the stage tabs make to their editor rows. */
export const TESTS_EDITOR_ID = "test:editor";

// Saved-test ids are client-local (tests have no backend yet) and stable
// across renames.
let localTestIdSeq = 0;

export function freshLocalTestId(): string {
  return `test:local:${localTestIdSeq++}`;
}

// Untouched blank lines (the "clear the only line" state) don't count as
// content — not for the dirty star, not in the read-only viewer, not when
// persisting.
const meaningfulAssertion = (r: TestAssertionDraft) =>
  r.path !== "" || r.value !== "";

/** The rows that count as content — untouched blank lines drop out. */
export function meaningfulAssertions(
  rows: ReadonlyArray<TestAssertionDraft>,
): TestAssertionDraft[] {
  return rows.filter(meaningfulAssertion);
}

export function testRowsEqual(
  a: ReadonlyArray<TestAssertionDraft>,
  b: ReadonlyArray<TestAssertionDraft>,
): boolean {
  const left = meaningfulAssertions(a);
  const right = meaningfulAssertions(b);
  return (
    left.length === right.length &&
    left.every(
      (r, i) =>
        r.path === right[i].path &&
        r.op === right[i].op &&
        r.value === right[i].value,
    )
  );
}

// ---------------------------------------------------------------------------
// Tests persistence: the suite-case mapping (old /studio's TestEditPanel
// value rules) — studio tests persist as cases of the staged suite.
// ---------------------------------------------------------------------------

// Tree suite rows — one per catalog suite (Figma Bridge_Explorer
// 14784-219168). The id names the suite so a drop can fetch its cases.
const SUITE_ROW_ID_PREFIX = "suite:";

export function suiteRowId(suiteId: string): string {
  return `${SUITE_ROW_ID_PREFIX}${suiteId}`;
}

export function isSuiteRowId(id: string | null): boolean {
  return id?.startsWith(SUITE_ROW_ID_PREFIX) ?? false;
}

/** The suite behind a tree suite row's id — suite ids may contain colons,
 *  so everything after the prefix is the id. */
export function parseSuiteRowId(id: string): string | null {
  if (!id.startsWith(SUITE_ROW_ID_PREFIX)) return null;
  const suiteId = id.slice(SUITE_ROW_ID_PREFIX.length);
  return suiteId.length > 0 ? suiteId : null;
}

// Schema rows are `schema:<service>:<entry id>` (TTTL-319): each
// schema-capable service has a store of its own, and entry ids are only
// unique within one, so the owning service rides in the id — the same
// shape as `resource:<service>:<name>`.
export function schemaRowId(serviceName: string, entryId: string): string {
  return `schema:${serviceName}:${entryId}`;
}

/** A tree/staged schema row's id → its service and store entry id, null
 *  for any other row (the schemas FOLDER is "schemas:<service>", not a
 *  schema) and for a service-less legacy id. */
export function parseSchemaRowId(
  rowId: string,
): { serviceName: string; entryId: string } | null {
  const [kind, serviceName, ...rest] = rowId.split(":");
  if (kind !== "schema") return null;
  if (!serviceName) return null;
  const entryId = rest.join(":");
  return entryId ? { serviceName, entryId } : null;
}

/** The editor's string form of a stored expected value. */
export function formatAssertionValue(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

/** The read-only viewer's value text (Figma Bridge_TestLine, 14785-268156):
 *  the typed value's JSON form — strings quoted, null/booleans/numbers/
 *  objects bare — so the line reads as the persisted assertion. */
export function assertionValueDisplay(value: string): string {
  return JSON.stringify(parseAssertionValue(value));
}

/** The typed value behind an assertion's text: null/booleans/numbers/JSON
 *  by shape, a plain string otherwise (spaces preserved). */
export function parseAssertionValue(str: string): unknown {
  const trimmed = str.trim();
  if (trimmed === "") return str;
  if (trimmed === "null") return null;
  if (trimmed === "true") return true;
  if (trimmed === "false") return false;
  const num = Number(trimmed);
  if (!Number.isNaN(num)) return num;
  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed === "object") return parsed;
  } catch {
    // Not JSON — a plain string.
  }
  return str;
}

/** A saved studio test as a stored suite case — untouched blank lines
 *  don't become assertions. Strict mode (the engine fails the case on any
 *  output field no assertion covers) travels with the case. */
export function suiteCaseFromTest(
  name: string,
  rows: ReadonlyArray<TestAssertionDraft>,
  strictMode = false,
): TestSuiteCase {
  return {
    name,
    assertions: meaningfulAssertions(rows).map((r) => ({
      path: r.path,
      assert_type: r.op,
      expected_value: parseAssertionValue(r.value),
    })),
    strict_mode: strictMode,
  };
}

// A test's strict flag lives beside its assertion rows: `saved` per
// staged test (from the stored case), `drafts` holding a working value
// only while it differs from the saved one — so "has an override" is
// "dirty", and Save/discard just move or drop the override.
export type StrictMap = Record<string, boolean>;

export function strictModeOf(
  id: string,
  saved: StrictMap,
  drafts: StrictMap,
): boolean {
  return drafts[id] ?? saved[id] ?? false;
}

export function withStrictOverride(
  drafts: StrictMap,
  id: string,
  value: boolean,
  saved: StrictMap,
): StrictMap {
  if (value === (saved[id] ?? false)) {
    const { [id]: _, ...rest } = drafts;
    return rest;
  }
  return { ...drafts, [id]: value };
}

/** Suite-scoped case edits — the write-through catalog ops build the next
 *  cases array with these before saving the suite back. Names are unique
 *  within a suite (the panel validates), so name is the case key. */
export function upsertCaseByName(
  cases: ReadonlyArray<TestSuiteCase>,
  next: TestSuiteCase,
): TestSuiteCase[] {
  return cases.some((c) => c.name === next.name)
    ? cases.map((c) => (c.name === next.name ? next : c))
    : [...cases, next];
}

export function renameCaseByName(
  cases: ReadonlyArray<TestSuiteCase>,
  oldName: string,
  newName: string,
): TestSuiteCase[] {
  return cases.map((c) => (c.name === oldName ? { ...c, name: newName } : c));
}

export function removeCaseByName(
  cases: ReadonlyArray<TestSuiteCase>,
  name: string,
): TestSuiteCase[] {
  return cases.filter((c) => c.name !== name);
}

/** A stored suite case back as editor lines; a case with no assertions
 *  opens on one fresh line. */
export function testRowsFromSuiteCase(
  suiteCase: TestSuiteCase,
): TestAssertionDraft[] {
  const rows = (suiteCase.assertions ?? []).map((a) => ({
    ...freshAssertionDraft(),
    path: a.path,
    op: a.assert_type as TestAssertionOp,
    value: formatAssertionValue(a.expected_value),
  }));
  return rows.length > 0 ? rows : [freshAssertionDraft()];
}
